import { useEffect, useRef, useState } from 'react'

/**
 * A stop before something irreversible.
 *
 * This replaced press-twice, where the second press landed on a row whose
 * label had changed under the pointer — fine when you are reading, and a
 * coin-flip when you are not. A dialog has to be aimed at, and it can say
 * what is about to go in a sentence the row had no space for.
 *
 * `confirmWord` adds a box the word has to be typed into. Reserved for what
 * takes the whole team's material with it: the cost of a mis-tap there is
 * somebody else's afternoon, not your own two minutes.
 */
export function ConfirmDialog({
  title,
  body,
  confirmLabel,
  confirmWord,
  busy,
  onCancel,
  onConfirm,
}: {
  title: string
  body: string
  confirmLabel: string
  confirmWord?: string
  busy?: boolean
  onCancel: () => void
  onConfirm: () => void
}) {
  const [typed, setTyped] = useState('')
  const ref = useRef<HTMLDivElement>(null)

  // Escape closes it, and the focus starts inside rather than wherever the
  // press left it — a dialog the keyboard cannot reach is decoration.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !busy) onCancel()
    }
    document.addEventListener('keydown', onKey)
    const focusable = ref.current?.querySelector<HTMLElement>('input, button')
    focusable?.focus()
    return () => document.removeEventListener('keydown', onKey)
  }, [busy, onCancel])

  // Exact, capitals and all. A word you have to copy carefully is the whole
  // point of asking for one; accepting "erase" made it a formality.
  const ready = !confirmWord || typed.trim() === confirmWord

  return (
    <div className="dialog-back" onMouseDown={() => !busy && onCancel()}>
      <div
        className="dialog"
        role="alertdialog"
        aria-modal="true"
        aria-label={title}
        ref={ref}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <h3 className="dialog-title">{title}</h3>
        <p className="dialog-body">{body}</p>

        {confirmWord && (
          <label className="field">
            <span>
              Type <b>{confirmWord}</b> to confirm
            </span>
            <input
              value={typed}
              onChange={(e) => setTyped(e.target.value)}
              // The word itself, greyed in the box: the label above says what
              // to type, and an empty box still left people wondering whether
              // the capitals mattered. They do — the match is exact.
              placeholder={confirmWord}
              autoCapitalize="characters"
              autoComplete="off"
              spellCheck={false}
              disabled={busy}
            />
          </label>
        )}

        <div className="dialog-actions">
          <button className="secondary" onClick={onCancel} disabled={busy}>
            Cancel
          </button>
          <button className="danger-btn" onClick={onConfirm} disabled={busy || !ready}>
            {busy ? 'Working…' : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  )
}
